/**
 * Tipos de movimiento de stock.
 *
 * `opening_balance` existe en los datos pero NO se ofrece en el alta manual:
 * es exclusivo de la carga inicial de saldos.
 */
export type StockMovementType = 'purchase' | 'sale' | 'inventory_adjustment' | 'opening_balance';

export const STOCK_MOVEMENT_TYPE_LABELS: Record<StockMovementType, string> = {
  purchase: 'Compra',
  sale: 'Venta',
  inventory_adjustment: 'Ajuste de inventario',
  opening_balance: 'Saldo inicial'
};

/** Tipos que el usuario puede dar de alta a mano desde el informe. */
export const MANUAL_STOCK_MOVEMENT_TYPES: StockMovementType[] = [
  'purchase',
  'sale',
  'inventory_adjustment'
];

/** Signo que aplica la UI para que el usuario solo teclee cantidades positivas. */
export const STOCK_MOVEMENT_SIGN: Record<StockMovementType, number> = {
  purchase: 1,
  sale: -1,
  inventory_adjustment: 1,
  opening_balance: 1
};

export interface StockMovement {
  id: string;
  warehouseId: string;
  warehouseName: string;
  bookId: string;
  bookTitle: string;
  bookIsbn: string;
  movementType: StockMovementType;
  quantity: number;
  movementDate: string;
  sourceTable: string;
  sourceRecordId: string;
}

export interface StockMovementPayload {
  warehouseId: string;
  bookId: string;
  movementType: StockMovementType;
  quantity: number;
  movementDate: string;
  sourceTable?: string;
  sourceRecordId?: string;
}

/**
 * Fila agregada del informe de auditoría: stock derivado por almacén y libro.
 *
 * `currentStock` es el saldo de sumar los movimientos con su signo, y es el
 * único dato de existencias fiable: `stockValue`/`averageStock`/`rotationIndex`
 * se calculan sobre él.
 *
 * `declaredStock` es lo que declara la ficha del libro (`books.stock`), y se
 * mantiene a la espera de que el backend lo guarde: hoy esa columna no existe
 * (ver mapBook en BooksService) y llega 0 en todas las filas. Es informativo y
 * NO se cruza contra `currentStock`: `books.stock` sería un valor por libro y
 * estas filas son por almacén, así que la resta no significaría nada.
 */
export interface StockAuditRow {
  warehouseId: string;
  warehouseName: string;
  bookId: string;
  bookTitle: string;
  bookIsbn: string;
  purchases: number;
  sales: number;
  adjustments: number;
  openingBalance: number;
  currentStock: number;
  movementCount: number;
  lastMovementDate: string;
  declaredStock: number;
  stockValue: number;
  averageStock: number;
  rotationIndex: number;
}
